/**
 * axon API SERVICE - PRODUCTION READY
 * All API calls for axon site - Backend Integration
 * 
 * @version 4.0.0
 * @production-ready
 * @author Ayana Renewable Power
 * @description Centralized API service for Databank, Ambient, BD Details, Meter Reading, and Master Sheet
 */

import { ApiResponse } from '../../../shared/services/baseApiService';
import backendApi from '../../../../../../services/backendApi';

// ==================== CONFIGURATION ====================

const API_BASE_URL = 'http://localhost:8000/api/v1';
const DEFAULT_SITE_CODE = 'axon';
const DEFAULT_DATE_RANGE_DAYS = 730;
const API_DEBUG = false; // Set true for debug logging, false for production speed // 2 years - ensures ALL historical data is found on first call

interface LoadByUserOptions {
  userId: number;
  siteCode?: string | null;
  siteId?: number | null;
}

type LoadByUserArg = number | LoadByUserOptions;

// Get site code from URL or use default
const getSiteCode = (): string => {
  // Try to get from URL - handles /entry/CODE, /upload/CODE, etc.
  const path = window.location.pathname;
  const match = path.match(/\/(?:entry|upload|view|navigate)\/([^\/]+)/i);
  if (match && match[1]) {
    return match[1].toLowerCase();
  }

  // Fallback to storage if not in URL
  const storedCode =
    sessionStorage.getItem('tenant') ||
    localStorage.getItem('currentSite') ||
    localStorage.getItem('site_code') ||
    sessionStorage.getItem('site_code');

  if (storedCode) return storedCode.toLowerCase();

  return DEFAULT_SITE_CODE;
};

const getStoredSiteId = (): number | null => {
  try {
    const stored =
      sessionStorage.getItem('site_id') ||
      localStorage.getItem('site_id');
    if (!stored) return null;
    const parsed = Number(stored);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

interface NormalizedLoadContext {
  userId: number;
  siteCode: string | null;
  siteId: number | null;
}

const normalizeLoadContext = (arg: LoadByUserArg): NormalizedLoadContext => {
  const fallbackSiteCode = getSiteCode();
  const fallbackSiteId = getStoredSiteId();

  if (typeof arg === 'number') {
    return {
      userId: arg,
      siteCode: fallbackSiteCode,
      siteId: fallbackSiteId
    };
  }

  return {
    userId: arg.userId,
    siteCode: (arg.siteCode ?? fallbackSiteCode) || null,
    siteId: arg.siteId ?? fallbackSiteId ?? null
  };
};

const extractRowUserId = (row: any): number | null => {
  if (row?.user_id !== undefined && row?.user_id !== null) {
    const id = Number(row.user_id);
    if (Number.isFinite(id)) return id;
  }
  if (row?.user && typeof row.user === 'object' && row.user.id !== undefined) {
    const id = Number(row.user.id);
    if (Number.isFinite(id)) return id;
  }
  if (row?.user !== undefined && row?.user !== null) {
    const id = Number(row.user);
    if (Number.isFinite(id)) return id;
  }
  return null;
};

const normalizeSiteCode = (code?: string | null): string | null => {
  if (typeof code !== 'string') return null;
  const trimmed = code.trim();
  return trimmed ? trimmed.toLowerCase() : null;
};

const rowMatchesSite = (row: any, ctx: NormalizedLoadContext, debug: boolean = false): boolean => {
  const targetCode = normalizeSiteCode(ctx.siteCode);
  const targetId = ctx.siteId ?? null;

  const rowCode = normalizeSiteCode(
    row?.site_code || row?.site?.site_code || row?.siteCode
  );
  const rawRowId = row?.site_id ?? row?.site?.site_id ?? row?.siteId;
  const rowId = rawRowId !== undefined && rawRowId !== null ? Number(rawRowId) : null;

  // If row has no site info at all (rare for new data but possible for legacy), assume it matches
  if (!rowCode && rowId === null) {
    return true;
  }

  // If we have a target site ID and a row site ID, they MUST match
  if (targetId !== null && rowId !== null) {
    if (rowId !== targetId) {
      return false;
    }
    // If ID matches, we are good! (ID is more reliable than code)
    return true;
  }

  // If we have a target site code, row must match (fallback if IDs don't match or are missing)
  if (targetCode && rowCode && rowCode !== targetCode) {
    return false;
  }

  return true;
};

const rowMatchesContext = (row: any, ctx: NormalizedLoadContext, debug: boolean = false): boolean => {
  const rowUserId = extractRowUserId(row);

  // Determine if this is an admin view or the user has admin role
  const role = (sessionStorage.getItem('user_role') || '').toLowerCase();
  const roleName = (sessionStorage.getItem('role_name') || '').toLowerCase();
  const isGlobalAdmin = sessionStorage.getItem('isGlobalAdmin') === 'true';
  const userIsAdmin = isGlobalAdmin || role.includes('admin') || roleName.includes('admin') || role.includes('ho') || roleName.includes('ho');

  const isAdminView = ctx.userId <= 0 || userIsAdmin;

  // For Admins, we don't filter by user_id - they see everything the backend returns for the site
  if (isAdminView) {
    return rowMatchesSite(row, ctx, debug);
  }

  // For Regular Users:
  // 1. Allow rows with no user (user_id === null)
  // 2. Allow rows specifically matching their user ID
  if (rowUserId !== null && rowUserId !== ctx.userId) {
    return false;
  }

  // Backup: if ID matches, trust the row even if site info is fuzzy
  if (rowUserId === ctx.userId && rowUserId !== null) {
    return true;
  }

  return rowMatchesSite(row, ctx, debug);
};

/**
 * Get authentication token from localStorage or return test token
 * Checks multiple possible token keys for compatibility
 */
const getAuthToken = (): string => {
  const token = sessionStorage.getItem('access_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('access_token') ||
    sessionStorage.getItem('token');

  return token || '';
};

// ==================== BASE REQUEST WRAPPER ====================

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | null | undefined>;
}

type ApiResponseWithHeaders<T> = ApiResponse<T> & {
  headers?: Record<string, string>;
};

const collectResponseHeaders = (response: Response): Record<string, string> => {
  const headers: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    headers[key] = value;
  });
  return headers;
};

/**
 * Generic API request handler with error handling, retry logic, and logging
 * @param endpoint - API endpoint (e.g., '/ambient/range')
 * @param options - Request options including method, body, params, retries
 * @param retryCount - Internal retry counter (default: 0)
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {},
  retryCount: number = 0
): Promise<ApiResponseWithHeaders<T>> {
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 1000; // Start with 1 second
  const RETRIABLE_STATUSES = [408, 429, 500, 502, 503, 504]; // Retry on these status codes

  try {
    const { params, ...fetchOptions } = options;

    // Build URL with query parameters
    // Ensure endpoint ends with / for Django REST Framework compatibility (except for query strings)
    let normalizedEndpoint = endpoint;
    // Only add trailing slash if endpoint doesn't already have one and doesn't contain query params
    if (!normalizedEndpoint.endsWith('/') && !normalizedEndpoint.includes('?')) {
      normalizedEndpoint = normalizedEndpoint + '/';
    }
    let url = `${API_BASE_URL}${normalizedEndpoint}`;
    if (params && !url.includes('?')) {
      const queryParams: Record<string, string> = {};
      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        queryParams[key] = String(value);
      });
      const queryString = new URLSearchParams(queryParams).toString();
      url += `?${queryString}`;
    }

    // Build headers with required authentication and site context
    const token = getAuthToken();
    const siteCode = getSiteCode();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Site-Code': siteCode,
    };

    // Only add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Merge with any custom headers from options
    if (fetchOptions.headers) {
      Object.assign(headers, fetchOptions.headers);
    }

    // Make request with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    let response: Response;
    try {
      response = await fetch(url, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
      });
    } catch (fetchError: any) {
      clearTimeout(timeoutId);

      // Handle timeout and network errors
      if (fetchError.name === 'AbortError') {
        const errorMsg = 'Request timeout - server took too long to respond';
        console.error(`[API Timeout] ${endpoint}:`, errorMsg);
        if (retryCount < MAX_RETRIES) {
          const delay = RETRY_DELAY_MS * Math.pow(2, retryCount);
await new Promise(resolve => setTimeout(resolve, delay));
          return apiRequest<T>(endpoint, options, retryCount + 1);
        }
        return {
          ok: false,
          status: 0,
          data: null,
          error: errorMsg
        };
      }

      // Network errors (connection refused, etc.)
      if (retryCount < MAX_RETRIES) {
        const delay = RETRY_DELAY_MS * Math.pow(2, retryCount);
await new Promise(resolve => setTimeout(resolve, delay));
        return apiRequest<T>(endpoint, options, retryCount + 1);
      }

      throw fetchError;
    } finally {
      clearTimeout(timeoutId);
    }

    // Handle error responses
    if (!response.ok) {
      // Check if we should retry this status code
      if (RETRIABLE_STATUSES.includes(response.status) && retryCount < MAX_RETRIES) {
        const delay = RETRY_DELAY_MS * Math.pow(2, retryCount);
await new Promise(resolve => setTimeout(resolve, delay));
        return apiRequest<T>(endpoint, options, retryCount + 1);
      }

      // Try to parse error response
      let errorData: any = {};
      let errorMessage = `Request failed: ${response.statusText}`;

      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
          errorMessage = errorData.detail || errorData.error || errorData.message || errorMessage;

          // Include field-specific errors if available
          if (errorData.errors || errorData.non_field_errors) {
            const fieldErrors = errorData.errors || errorData.non_field_errors;
            if (typeof fieldErrors === 'object') {
              const errorParts = Object.entries(fieldErrors).map(([key, value]) => {
                if (Array.isArray(value)) {
                  return `${key}: ${value.join(', ')}`;
                }
                return `${key}: ${value}`;
              });
              errorMessage = errorParts.length > 0 ? errorParts.join('; ') : errorMessage;
            }
          }
        } else {
          const text = await response.text();
          if (text && text.length < 500) { // Only log short error messages
            errorMessage = text;
          }
        }
      } catch (parseError) {
        // If we can't parse the error, use the status text
}

      console.error(`[API Error] ${response.status}:`, errorMessage);

      return {
        ok: false,
        status: response.status,
        data: null,
        error: errorMessage,
        headers: collectResponseHeaders(response)
      };
    }

    // Handle no content responses
    if (response.status === 204) {
      return {
        ok: true,
        status: 204,
        data: undefined as any,
        error: null,
        headers: collectResponseHeaders(response)
      };
    }

    // Parse and return successful response
    try {
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
return {
          ok: false,
          status: response.status,
          data: null,
          error: `Expected JSON response but got ${contentType}`
        };
      }

      const data = await response.json();
      return {
        ok: true,
        status: response.status,
        data,
        error: null,
        headers: collectResponseHeaders(response)
      };
    } catch (parseError: any) {
      console.error(`[API] JSON parse error:`, parseError);
      return {
        ok: false,
        status: response.status,
        data: null,
        error: `Failed to parse response: ${parseError.message}`,
        headers: collectResponseHeaders(response)
      };
    }
  } catch (error: any) {
    // Final catch for any unexpected errors
    console.error(`[API Error] ${endpoint}:`, error);

    // Retry on unexpected errors if we haven't exceeded max retries
    if (retryCount < MAX_RETRIES && error.message && !error.message.includes('aborted')) {
      const delay = RETRY_DELAY_MS * Math.pow(2, retryCount);
await new Promise(resolve => setTimeout(resolve, delay));
      return apiRequest<T>(endpoint, options, retryCount + 1);
    }

    return {
      ok: false,
      status: 0,
      data: null,
      error: error.message || 'Network error occurred'
    };
  }
}

// ==================== UTILITY FUNCTIONS ====================

/**
 * Convert array of objects to CSV string
 */
function convertToCSV(data: any[], headers: string[]): string {
  const rows = data.map(row =>
    headers.map(header => {
      const value = row[header];
      // Escape values with commas or quotes
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value ?? '';
    }).join(',')
  );
  return [headers.join(','), ...rows].join('\n');
}

/**
 * Download CSV file to user's computer
 */
function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Get date range for default queries (last N days)
 */
function getDefaultDateRange(days: number = DEFAULT_DATE_RANGE_DAYS): { start: string; end: string } {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  return {
    start: startDate.toISOString().split('T')[0],
    end: endDate.toISOString().split('T')[0]
  };
}

// ==================== SMART IN-MEMORY CACHE ====================

const CACHE_TTL_MS = 10 * 60 * 1000; // 10-minute cache TTL (reduced re-fetches for speed)
const LOADBYUSER_DATE_RANGE_DAYS = 730; // 2 years - ensures ALL data found on FIRST call (no fallbacks needed)

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

const _apiCache = new Map<string, CacheEntry<any>>();

function getCacheKey(service: string, userId: number, siteCode: string | null): string {
  return `${service}_${userId}_${siteCode || 'default'}`;
}

function getFromCache<T>(key: string): T | null {
  const entry = _apiCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    _apiCache.delete(key);
    return null;
  }
  return entry.data as T;
}

function setCache<T>(key: string, data: T): void {
  _apiCache.set(key, {
    data,
    timestamp: Date.now(),
    expiresAt: Date.now() + CACHE_TTL_MS,
  });
}

function invalidateCache(service: string): void {
  const prefix = `${service}_`;
  for (const key of _apiCache.keys()) {
    if (key.startsWith(prefix)) {
      _apiCache.delete(key);
    }
  }
}

function invalidateAllCache(): void {
  _apiCache.clear();
}

/** Get optimized date range for loadByUser (90 days instead of 10 years) */
function getLoadByUserDateRange(): { start: string; end: string } {
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 7); // 1 week forward buffer
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - LOADBYUSER_DATE_RANGE_DAYS);
  return {
    start: startDate.toISOString().split('T')[0],
    end: endDate.toISOString().split('T')[0],
  };
}

// ==================== TYPE DEFINITIONS ====================

export interface DatabankDataRow {
  id?: number; // ID for updating existing records
  entry_date: string;
  icr: string;
  inverter: string;
  energy_kwh: number;
  site_code?: string; // Optional, defaults to 'axon' in backend
}

export interface AmbientDataRow {
  id?: number; // ID for updating existing records
  entry_date: string;
  site_code?: string; // Optional, defaults to 'axon' in backend
  sunrise_time?: string | null;
  sunset_time?: string | null;
  uptime_minutes?: number | null;
  ghi_kwh_per_m2?: number | null;
  poa_kwh_per_m2?: number | null;
  avg_ambient_temp_c?: number | null;
  max_ambient_temp_c?: number | null;
  avg_module_temp_c?: number | null;
  max_module_temp_c?: number | null;
  avg_air_speed_ms?: number | null;
  max_air_speed_ms?: number | null;
  weather_status?: string | null;
}

export interface BDDetailsDataRow {
  id?: number;
  entry_date: string;
  site_code?: string; // Optional, defaults to 'axon' in backend
  budget_pr_for_month?: string | null;
  equipment?: string | null;
  bd_category?: string | null;
  icr_no?: string | null;
  inverter_no?: string | null;
  bd_from?: string | null;
  bd_up_to?: string | null;
  bd_minutes?: number | null;
  bd_hours?: number | null;
  pa_bd_hours?: number | null;
  dc_capacity_kw?: number | null;
  poa_lost_kwh_per_m2?: number | null;
  c_a_times_b?: number | null;
  generation_loss_kwh?: number | null;
  scb_qs_unit?: string | null;
  bd_description?: string | null;
  corrective_action_taken?: string | null;
  current_status?: string | null;
  effective_loss_of_poa?: string | null;
  scope?: string | null;
  equipment_off_condition?: string | null;
  cause_error_code?: string | null;
}

export interface MeterReadingDataRow {
  id?: number;
  entry_date: string;
  site_code?: string; // Optional, defaults to 'axon' in backend
  feeder1_initial_export?: number | null;
  feeder1_final_export?: number | null;
  feeder1_net_kwh?: number | null;
  inverter_generation_600v?: number | null;
  feeder1_initial_import_kwh?: number | null;
  feeder1_final_import_kwh?: number | null;
  'Net Import'?: number | null;
}

export interface MasterSheetDataRow {
  id?: number; // ID for updating existing records
  entry_date: string;
  site_code?: string; // Optional, defaults to 'axon' in backend
  [key: string]: any;
}

// ==================== DATABANK SERVICES ====================

export const databankService = {
  /**
   * Load all databank entries for given dates or last 30 days
   */
  loadAll: async (dates?: string[]): Promise<ApiResponse<DatabankDataRow[]>> => {
    try {
      if (!dates || dates.length === 0) {
        const { start, end } = getDefaultDateRange();
        return apiRequest<DatabankDataRow[]>('/databank/range/', {
          params: { start_date: start, end_date: end, limit: 99999 }
        });
      }

      let allData: DatabankDataRow[] = [];
      for (const date of dates) {
        const response = await apiRequest<DatabankDataRow[]>(`/databank/date/${date}`);
        if (response.ok && response.data) {
          allData.push(...response.data);
        }
      }

      return {
        ok: true,
        status: 200,
        data: allData,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  loadAggregated: async (date: string): Promise<ApiResponse<any>> => {
    return apiRequest<any>(`/databank/aggregated/${date}`);
  },

  save: async (data: DatabankDataRow): Promise<ApiResponse<any>> => {
    invalidateCache('databank');
    return apiRequest<any>('/databank/bulk/', {
      method: 'POST',
      body: JSON.stringify([data])
    });
  },

  saveBatch: async (data: DatabankDataRow[]): Promise<ApiResponse<any>> => {
    if (data.length === 0) {
      return { ok: true, status: 200, data: { message: 'No data to save' }, error: null };
    }
    invalidateCache('databank');
    return apiRequest<any>('/databank/bulk/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  export: async (dates: string[]): Promise<ApiResponse<void>> => {
    try {
      // Get current user ID and site code
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        throw new Error('Unable to identify user. Please login again.');
      }

      const token = getAuthToken();
      const siteCode = getSiteCode();

      // Use backend Excel export endpoint
      const response = await fetch(
        `${API_BASE_URL}/export-to-excel/databank/${siteCode}/`,
        {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ dates: dates || [] })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Export failed: ${response.status}`);
      }

      // Download Excel file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const dateRange = dates && dates.length > 0
        ? `${dates[0]}_to_${dates[dates.length - 1]}`
        : new Date().toISOString().split('T')[0];
      const sheetName = 'Inverters';
      a.download = `${sheetName} - ${siteCode} - ${dateRange}.xlsx`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return {
        ok: true,
        status: 200,
        data: null,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  update: async (
    entry_date: string,
    icr: string,
    inverter: string,
    energy_kwh: number
  ): Promise<ApiResponse<any>> => {
    invalidateCache('databank');
    return apiRequest<any>(`/databank/entry/${entry_date}/${icr}/${inverter}/`, {
      method: 'PUT',
      body: JSON.stringify({ energy_kwh })
    });
  },

  delete: async (
    entry_date: string,
    icr: string,
    inverter: string
  ): Promise<ApiResponse<void>> => {
    invalidateCache('databank');
    return apiRequest<void>(`/databank/entry/${entry_date}/${icr}/${inverter}/`, {
      method: 'DELETE'
    });
  },

  /**
   * Load databank entries for the current user/site combination.
   * Enforces user_id + site_code/site_id verification on the client.
   */
  loadByUser: async (args: LoadByUserArg): Promise<ApiResponseWithHeaders<DatabankDataRow[]>> => {
    try {
      const ctx = normalizeLoadContext(args);
      if (!Number.isFinite(ctx.userId)) {
        return { ok: false, status: 400, data: null, error: 'Invalid user ID for Databank load' };
      }

      // Check cache first
      const cacheKey = getCacheKey('databank', ctx.userId, ctx.siteCode);
      const cached = getFromCache<DatabankDataRow[]>(cacheKey);
      if (cached) {
        return { ok: true, status: 200, data: cached, error: null };
      }

      const isAdminView = ctx.userId <= 0;
      const { start, end } = getLoadByUserDateRange();

      // Databank rows are far denser than the other sheets (~200 rows/day, one per
      // ICR x inverter combination), so the backend's `/databank/range/` endpoint
      // caps `limit` server-side regardless of what's requested here. A single call
      // with `limit: 99999` used to work when the endpoint was unbounded, but now
      // silently returns only a couple of days' worth. Page through the full result
      // instead (same pattern already used for the date-filtered load below).
      const rows = await loadAllPaginatedData(
        (params) => apiRequest<DatabankDataRow[]>('/databank/range/', {
          params: {
            start_date: params.startDate,
            end_date: params.endDate,
            offset: params.offset,
            limit: params.limit,
            ...(isAdminView ? { all_users: 'true' } : {})
          }
        }) as any,
        { startDate: start, endDate: end, initialLimit: 400 }
      );

      setCache(cacheKey, rows);
      return { ok: true, status: 200, data: rows, error: null };
    } catch (error: any) {
      return { ok: false, status: 0, data: null, error: error.message };
    }
  },

  /**
   * Update existing databank entry by ID - Simple update, only sends energy_kwh
   */
  updateById: async (id: number, data: Partial<DatabankDataRow>): Promise<ApiResponse<DatabankDataRow>> => {
    invalidateCache('databank');
    const updateData: any = {};
    if (data.energy_kwh !== undefined && data.energy_kwh !== null) {
      updateData.energy_kwh = data.energy_kwh;
    }
    return apiRequest<DatabankDataRow>(`/databank/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  },

  /**
   * Load databank data by date range for lazy loading
   */
  loadByDateRange: async (ctx: NormalizedLoadContext & { startDate: string; endDate: string; offset?: number; limit?: number }): Promise<ApiResponse<DatabankDataRow[]>> => {
    try {
      const response = await apiRequest<DatabankDataRow[]>('/databank/range/', {
        params: {
          start_date: ctx.startDate,
          end_date: ctx.endDate,
          offset: ctx.offset,
          limit: ctx.limit
        }
      });

      if (response.ok && response.data) {
        // Backend handles role-based filtering via get_queryset(); no client-side re-filter needed.
        return { ok: true, status: 200, data: response.data, error: null };
      }

      return response;
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  }
};

// ==================== AMBIENT SERVICES ====================

export const ambientService = {
  loadAll: async (dates?: string[]): Promise<ApiResponse<AmbientDataRow[]>> => {
    try {
      if (!dates || dates.length === 0) {
        const { start, end } = getDefaultDateRange();
        const response = await apiRequest<AmbientDataRow[]>('/ambient/range/', {
          params: { start_date: start, end_date: end, limit: 99999 }
        });

        // Handle 404 or 405 errors gracefully (endpoint might not exist or method not allowed)
        if (response.status === 404 || response.status === 405) {
return {
            ok: true,
            status: 200,
            data: [],
            error: null
          };
        }

        return response;
      }

      let allData: AmbientDataRow[] = [];
      for (const date of dates) {
        const response = await apiRequest<AmbientDataRow>(`/ambient/date/${date}`);
        if (response.ok && response.data) {
          allData.push(response.data);
        }
      }

      return {
        ok: true,
        status: 200,
        data: allData,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  /**
   * Find existing Ambient record by entry_date and site
   */
  findByDate: async (entry_date: string): Promise<ApiResponse<AmbientDataRow | null>> => {
    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        return {
          ok: false,
          status: 401,
          data: null,
          error: 'User not authenticated'
        };
      }

      const userId = userResponse.user.id;
      const response = await ambientService.loadByUser(userId);

      if (response.ok && response.data) {
        const existing = response.data.find((row: any) =>
          row.entry_date === entry_date
        );
        return {
          ok: true,
          status: 200,
          data: existing || null,
          error: null
        };
      }

      return {
        ok: true,
        status: 200,
        data: null,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  save: async (data: AmbientDataRow): Promise<ApiResponse<AmbientDataRow>> => {
    invalidateCache('ambient');

    // If ID exists, update existing record
    if (data.id && typeof data.id === 'number') {
      return ambientService.update(data.id, data);
    }

    // Check for existing record by entry_date
    const existingResult = await ambientService.findByDate(data.entry_date);
    if (existingResult.ok && existingResult.data && existingResult.data.id) {
      return ambientService.update(existingResult.data.id, data);
    }

    // Create new record
    return apiRequest<AmbientDataRow>('/ambient/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  update: async (id: number, data: Partial<AmbientDataRow>): Promise<ApiResponse<AmbientDataRow>> => {
    invalidateCache('ambient');
    const updateData = { ...data };
    delete updateData.id;
    delete updateData.site_code;
    return apiRequest<AmbientDataRow>(`/ambient/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  },

  export: async (dates: string[]): Promise<ApiResponse<void>> => {
    try {
      // Get current user ID and site code
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        throw new Error('Unable to identify user. Please login again.');
      }

      const token = getAuthToken();
      const siteCode = getSiteCode();

      // Use backend Excel export endpoint
      const response = await fetch(
        `${API_BASE_URL}/export-to-excel/ambient/${siteCode}/`,
        {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ dates: dates || [] })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Export failed: ${response.status}`);
      }

      // Download Excel file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const dateRange = dates && dates.length > 0
        ? `${dates[0]}_to_${dates[dates.length - 1]}`
        : new Date().toISOString().split('T')[0];
      const sheetName = 'Weather';
      a.download = `${sheetName} - ${siteCode} - ${dateRange}.xlsx`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return {
        ok: true,
        status: 200,
        data: null,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  delete: async (entry_date: string): Promise<ApiResponse<void>> => {
    invalidateCache('ambient');
    return apiRequest<void>(`/ambient/date/${entry_date}/`, {
      method: 'DELETE'
    });
  },

  /**
   * Load all ambient entries for the current user
   */
  loadByUser: async (args: LoadByUserArg): Promise<ApiResponseWithHeaders<AmbientDataRow[]>> => {
    try {
      const ctx = normalizeLoadContext(args);
      if (!Number.isFinite(ctx.userId)) {
        return { ok: false, status: 400, data: null, error: 'Invalid user ID for Ambient load' };
      }

      // Check cache first
      const cacheKey = getCacheKey('ambient', ctx.userId, ctx.siteCode);
      const cached = getFromCache<AmbientDataRow[]>(cacheKey);
      if (cached) {
        return { ok: true, status: 200, data: cached, error: null };
      }

      const isAdminView = ctx.userId <= 0;
      const { start, end } = getLoadByUserDateRange();

      const response = await apiRequest<AmbientDataRow[]>('/ambient/range/', {
        params: { start_date: start, end_date: end, limit: 99999, ...(isAdminView ? { all_users: 'true' } : {}) }
      });

      if (response.ok && response.data) {
        // Backend handles role-based filtering via get_queryset(); no client-side re-filter needed.
        setCache(cacheKey, response.data);
        return { ok: true, status: 200, data: response.data, error: null, headers: response.headers };
      }

      return response;
    } catch (error: any) {
      return { ok: false, status: 0, data: null, error: error.message };
    }
  },

  /**
   * Load ambient entries by date range with user/site filtering
   */
  loadByDateRange: async (ctx: NormalizedLoadContext & { startDate?: string; endDate?: string; offset?: number; limit?: number; cursorDate?: string; cursorId?: string }): Promise<ApiResponseWithHeaders<AmbientDataRow[]>> => {
    try {
      if (!Number.isFinite(ctx.userId)) {
        return { ok: false, status: 400, data: null, error: 'Invalid user ID for Ambient load' };
      }

      const isAdminView = ctx.userId <= 0;
      const start = ctx.startDate || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const end = ctx.endDate || new Date().toISOString().split('T')[0];

      const response = await apiRequest<AmbientDataRow[]>('/ambient/range/', {
        params: {
          start_date: start,
          end_date: end,
          offset: ctx.offset,
          limit: ctx.limit,
          cursor_date: ctx.cursorDate,
          cursor_id: ctx.cursorId
        }
      });

      if (response.ok && response.data) {
        // Backend handles role-based filtering via get_queryset(); no client-side re-filter needed.
        return { ok: true, status: 200, data: response.data, error: null, headers: response.headers };
      }

      return response;
    } catch (error: any) {
      return { ok: false, status: 0, data: null, error: error.message };
    }
  }
};

// ==================== BD DETAILS SERVICES ====================

export const bdDetailsService = {
  loadAll: async (dates?: string[]): Promise<ApiResponse<BDDetailsDataRow[]>> => {
    try {
      if (!dates || dates.length === 0) {
        const { start, end } = getDefaultDateRange();
        const response = await apiRequest<BDDetailsDataRow[]>('/bd-details/range/', {
          params: { start_date: start, end_date: end, limit: 99999 }
        });

        // Handle 404 or 405 errors gracefully
        if (response.status === 404 || response.status === 405) {
return {
            ok: true,
            status: 200,
            data: [],
            error: null
          };
        }

        return response;
      }

      let allData: BDDetailsDataRow[] = [];
      for (const date of dates) {
        const response = await apiRequest<BDDetailsDataRow[]>(`/bd-details/date/${date}`);

        if (response.ok && response.data) {
          allData.push(...response.data);
        } else if (response.status !== 404) {
}
      }

      return {
        ok: true,
        status: 200,
        data: allData,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  /**
   * Find existing BD Details record by entry_date (returns first match)
   * Note: BD Details can have multiple records per date, so this returns the first one
   */
  findByDate: async (entry_date: string): Promise<ApiResponse<BDDetailsDataRow | null>> => {
    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        return {
          ok: false,
          status: 401,
          data: null,
          error: 'User not authenticated'
        };
      }

      const userId = userResponse.user.id;
      const response = await bdDetailsService.loadByUser(userId);

      if (response.ok && response.data) {
        const existing = response.data.find((row: any) =>
          row.entry_date === entry_date
        );
        return {
          ok: true,
          status: 200,
          data: existing || null,
          error: null
        };
      }

      return {
        ok: true,
        status: 200,
        data: null,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  save: async (data: BDDetailsDataRow): Promise<ApiResponse<BDDetailsDataRow>> => {
    invalidateCache('bddetails');

    // If ID exists, update existing record
    if (data.id && typeof data.id === 'number') {
      return bdDetailsService.update(data.id, data);
    }

    // Create new record
    return apiRequest<BDDetailsDataRow>('/bd-details/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  update: async (id: number, data: Partial<BDDetailsDataRow>): Promise<ApiResponse<BDDetailsDataRow>> => {
    invalidateCache('bddetails');
    const updateData = { ...data };
    delete updateData.id;
    delete updateData.site_code;
    return apiRequest<BDDetailsDataRow>(`/bd-details/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  },

  delete: async (id: number): Promise<ApiResponse<void>> => {
    invalidateCache('bddetails');
    return apiRequest<void>(`/bd-details/${id}`, {
      method: 'DELETE'
    });
  },

  export: async (dates: string[]): Promise<ApiResponse<void>> => {
    try {
      // Get current user ID and site code
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        throw new Error('Unable to identify user. Please login again.');
      }

      const token = getAuthToken();
      const siteCode = getSiteCode();

      // Use backend Excel export endpoint
      const response = await fetch(
        `${API_BASE_URL}/export-to-excel/bd/${siteCode}/`,
        {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ dates: dates || [] })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Export failed: ${response.status}`);
      }

      // Download Excel file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const dateRange = dates && dates.length > 0
        ? `${dates[0]}_to_${dates[dates.length - 1]}`
        : new Date().toISOString().split('T')[0];
      const sheetName = 'Breakdowns';
      a.download = `${sheetName} - ${siteCode} - ${dateRange}.xlsx`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return {
        ok: true,
        status: 200,
        data: null,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  /**
   * Load all BD Details entries for the current user
   */
  loadByUser: async (args: LoadByUserArg): Promise<ApiResponseWithHeaders<BDDetailsDataRow[]>> => {
    try {
      const ctx = normalizeLoadContext(args);
      if (!Number.isFinite(ctx.userId)) {
        return { ok: false, status: 400, data: null, error: 'Invalid user ID for BD Details load' };
      }

      // Check cache first
      const cacheKey = getCacheKey('bddetails', ctx.userId, ctx.siteCode);
      const cached = getFromCache<BDDetailsDataRow[]>(cacheKey);
      if (cached) {
        return { ok: true, status: 200, data: cached, error: null };
      }

      const isAdminView = ctx.userId <= 0;
      const { start, end } = getLoadByUserDateRange();

      const response = await apiRequest<BDDetailsDataRow[]>('/bd-details/range/', {
        params: { start_date: start, end_date: end, limit: 99999, ...(isAdminView ? { all_users: 'true' } : {}) }
      });

      if (response.ok && response.data) {
        // Backend handles role-based filtering via get_queryset(); no client-side re-filter needed.
        setCache(cacheKey, response.data);
        return { ok: true, status: 200, data: response.data, error: null, headers: response.headers };
      }

      return response;
    } catch (error: any) {
      return { ok: false, status: 0, data: null, error: error.message };
    }
  },

  /**
   * Load BD Details entries by date range with cursor-based pagination.
   * Backend returns { data: [], total_count, has_more, next_cursor_date, next_cursor_id }
   */
  loadByDateRange: async (ctx: NormalizedLoadContext & { startDate?: string; endDate?: string; offset?: number; limit?: number; cursorDate?: string; cursorId?: string }): Promise<ApiResponseWithHeaders<BDDetailsDataRow[]> & { meta?: { total_count: number | null; has_more: boolean; next_cursor_date: string | null; next_cursor_id: string | null } }> => {
    try {
      if (!Number.isFinite(ctx.userId)) {
        return { ok: false, status: 400, data: null, error: 'Invalid user ID for BD Details load' };
      }

      const isAdminView = ctx.userId <= 0;
      // Build params — only include date range if explicitly provided
      const params: Record<string, string | number | boolean | null | undefined> = {
        offset: ctx.cursorDate ? undefined : ctx.offset,
        limit: ctx.limit,
        cursor_date: ctx.cursorDate,
        cursor_id: ctx.cursorId,
        ...(isAdminView ? { all_users: 'true' } : {})
      };
      // Only add date filters if provided (initial load has no dates = get newest data)
      if (ctx.startDate) params.start_date = ctx.startDate;
      if (ctx.endDate) params.end_date = ctx.endDate;

      const response = await apiRequest<any>('/bd-details/range/', { params });

      if (response.ok && response.data) {
        // Handle new object format: { data: [], total_count, has_more, ... }
        const isNewFormat = response.data && typeof response.data === 'object' && !Array.isArray(response.data) && 'data' in response.data;
        
        if (isNewFormat) {
          const body = response.data;
          return {
            ok: true,
            status: 200,
            data: body.data || [],
            error: null,
            headers: response.headers,
            meta: {
              total_count: body.total_count ?? null,
              has_more: body.has_more ?? false,
              next_cursor_date: body.next_cursor_date ?? null,
              next_cursor_id: body.next_cursor_id ?? null,
            }
          };
        }

        // Fallback: old flat array format
        return { ok: true, status: 200, data: response.data, error: null, headers: response.headers };
      }

      return response;
    } catch (error: any) {
      return { ok: false, status: 0, data: null, error: error.message };
    }
  }
};

// ==================== METER READING SERVICES ====================

export const meterReadingService = {
  loadAll: async (dates?: string[]): Promise<ApiResponse<MeterReadingDataRow[]>> => {
    try {
      if (!dates || dates.length === 0) {
        const { start, end } = getDefaultDateRange();
        const response = await apiRequest<MeterReadingDataRow[]>('/meter-reading/range/', {
          params: { start_date: start, end_date: end, limit: 99999 }
        });

        // Handle 404 or 405 errors gracefully
        if (response.status === 404 || response.status === 405) {
return {
            ok: true,
            status: 200,
            data: [],
            error: null
          };
        }

        return response;
      }

      let allData: MeterReadingDataRow[] = [];
      for (const date of dates) {
        const response = await apiRequest<MeterReadingDataRow>(`/meter-reading/date/${date}`);

        if (response.ok && response.data) {
          allData.push(response.data);
        } else if (response.status !== 404) {
}
      }

      return {
        ok: true,
        status: 200,
        data: allData,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  /**
   * Find existing Meter Reading record by entry_date and site
   */
  findByDate: async (entry_date: string): Promise<ApiResponse<MeterReadingDataRow | null>> => {
    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        return {
          ok: false,
          status: 401,
          data: null,
          error: 'User not authenticated'
        };
      }

      const userId = userResponse.user.id;
      const response = await meterReadingService.loadByUser(userId);

      if (response.ok && response.data) {
        const existing = response.data.find((row: any) =>
          row.entry_date === entry_date
        );
        return {
          ok: true,
          status: 200,
          data: existing || null,
          error: null
        };
      }

      return {
        ok: true,
        status: 200,
        data: null,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  save: async (data: MeterReadingDataRow): Promise<ApiResponse<MeterReadingDataRow>> => {
    invalidateCache('meterreading');

    // If ID exists, update existing record
    if (data.id && typeof data.id === 'number') {
      return meterReadingService.update(data.id, data);
    }

    // Check for existing record by entry_date
    const existingResult = await meterReadingService.findByDate(data.entry_date);
    if (existingResult.ok && existingResult.data && existingResult.data.id) {
      return meterReadingService.update(existingResult.data.id, data);
    }

    // Create new record
    return apiRequest<MeterReadingDataRow>('/meter-reading/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  delete: async (entry_date: string): Promise<ApiResponse<void>> => {
    invalidateCache('meterreading');
    return apiRequest<void>(`/meter-reading/date/${entry_date}/`, {
      method: 'DELETE'
    });
  },

  export: async (dates: string[]): Promise<ApiResponse<void>> => {
    try {
      // Get current user ID and site code
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        throw new Error('Unable to identify user. Please login again.');
      }

      const token = getAuthToken();
      const siteCode = getSiteCode();

      // Use backend Excel export endpoint
      const response = await fetch(
        `${API_BASE_URL}/export-to-excel/meter-reading/${siteCode}/`,
        {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ dates: dates || [] })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Export failed: ${response.status}`);
      }

      // Download Excel file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const dateRange = dates && dates.length > 0
        ? `${dates[0]}_to_${dates[dates.length - 1]}`
        : new Date().toISOString().split('T')[0];
      const sheetName = 'Plant Meters';
      a.download = `${sheetName} - ${siteCode} - ${dateRange}.xlsx`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return {
        ok: true,
        status: 200,
        data: null,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  /**
   * Load all Meter Reading entries for the current user
   */
  loadByUser: async (args: LoadByUserArg): Promise<ApiResponseWithHeaders<MeterReadingDataRow[]>> => {
    try {
      const ctx = normalizeLoadContext(args);
      if (!Number.isFinite(ctx.userId)) {
        return { ok: false, status: 400, data: null, error: 'Invalid user ID for Meter Reading load' };
      }

      // Check cache first
      const cacheKey = getCacheKey('meterreading', ctx.userId, ctx.siteCode);
      const cached = getFromCache<MeterReadingDataRow[]>(cacheKey);
      if (cached) {
        return { ok: true, status: 200, data: cached, error: null };
      }

      const isAdminView = ctx.userId <= 0;
      const { start, end } = getLoadByUserDateRange();

      const response = await apiRequest<MeterReadingDataRow[]>('/meter-reading/range/', {
        params: { start_date: start, end_date: end, limit: 99999, ...(isAdminView ? { all_users: 'true' } : {}) }
      });

      if (response.ok && response.data) {
        // Backend handles role-based filtering via get_queryset(); no client-side re-filter needed.
        setCache(cacheKey, response.data);
        return { ok: true, status: 200, data: response.data, error: null, headers: response.headers };
      }

      return response;
    } catch (error: any) {
      return { ok: false, status: 0, data: null, error: error.message };
    }
  },

  /**
   * Load Meter Reading entries by date range with user/site filtering
   */
  loadByDateRange: async (ctx: NormalizedLoadContext & { startDate?: string; endDate?: string; offset?: number; limit?: number; cursorDate?: string; cursorId?: string }): Promise<ApiResponseWithHeaders<MeterReadingDataRow[]>> => {
    try {
      if (!Number.isFinite(ctx.userId)) {
        return { ok: false, status: 400, data: null, error: 'Invalid user ID for Meter Reading load' };
      }

      const isAdminView = ctx.userId <= 0;
      const start = ctx.startDate || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const end = ctx.endDate || new Date().toISOString().split('T')[0];

      const response = await apiRequest<MeterReadingDataRow[]>('/meter-reading/range/', {
        params: {
          start_date: start,
          end_date: end,
          offset: ctx.offset,
          limit: ctx.limit,
          cursor_date: ctx.cursorDate,
          cursor_id: ctx.cursorId
        }
      });

      if (response.ok && response.data) {
        // Backend handles role-based filtering via get_queryset(); no client-side re-filter needed.
        return { ok: true, status: 200, data: response.data, error: null, headers: response.headers };
      }

      return response;
    } catch (error: any) {
      return { ok: false, status: 0, data: null, error: error.message };
    }
  },

  /**
   * Update existing meter reading entry by ID
   */
  update: async (id: number, data: Partial<MeterReadingDataRow>): Promise<ApiResponse<MeterReadingDataRow>> => {
    invalidateCache('meterreading');
    const updateData = { ...data };
    delete updateData.id;
    delete updateData.site_code;
    return apiRequest<MeterReadingDataRow>(`/meter-reading/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }
};

// ==================== MASTER SHEET SERVICES ====================

export const masterSheetService = {
  loadAll: async (dates?: string[]): Promise<ApiResponse<MasterSheetDataRow[]>> => {
    try {
      if (!dates || dates.length === 0) {
        const { start, end } = getDefaultDateRange();
        const response = await apiRequest<MasterSheetDataRow[]>('/master-sheet/range/', {
          params: { start_date: start, end_date: end, limit: 99999 }
        });

        // Handle 404 or 405 errors gracefully
        if (response.status === 404 || response.status === 405) {
return {
            ok: true,
            status: 200,
            data: [],
            error: null
          };
        }

        return response;
      }

      let allData: MasterSheetDataRow[] = [];
      for (const date of dates) {
        const response = await apiRequest<MasterSheetDataRow[]>(`/master-sheet/?entry_date=${date}`);
        if (response.ok && response.data) {
          if (Array.isArray(response.data)) {
            allData.push(...response.data);
          } else {
            allData.push(response.data);
          }
        }
      }

      return {
        ok: true,
        status: 200,
        data: allData,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  /**
   * Find existing Master Sheet record by entry_date and site
   */
  findByDate: async (entry_date: string, site_code?: string): Promise<ApiResponse<MasterSheetDataRow | null>> => {
    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        return {
          ok: false,
          status: 401,
          data: null,
          error: 'User not authenticated'
        };
      }

      const userId = userResponse.user.id;
      const response = await masterSheetService.loadByUser(userId);

      if (response.ok && response.data) {
        const existing = response.data.find((row: any) =>
          row.entry_date === entry_date
        );
        return {
          ok: true,
          status: 200,
          data: existing || null,
          error: null
        };
      }

      return {
        ok: true,
        status: 200,
        data: null,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  save: async (data: MasterSheetDataRow): Promise<ApiResponse<MasterSheetDataRow>> => {
    invalidateCache('mastersheet');

    // If ID exists, update existing record
    if (data.id && typeof data.id === 'number') {
      return masterSheetService.update(data.id, data);
    }

    // Check for existing record by entry_date
    const existingResult = await masterSheetService.findByDate(data.entry_date, data.site_code);
    if (existingResult.ok && existingResult.data && existingResult.data.id) {
      return masterSheetService.update(existingResult.data.id, data);
    }

    // Create new record
    return apiRequest<MasterSheetDataRow>('/master-sheet/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  update: async (id: number, data: Partial<MasterSheetDataRow>): Promise<ApiResponse<MasterSheetDataRow>> => {
    invalidateCache('mastersheet');
    const updateData = { ...data };
    delete updateData.id;
    delete updateData.site_code;
    return apiRequest<MasterSheetDataRow>(`/master-sheet/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  },

  export: async (dates: string[]): Promise<ApiResponse<void>> => {
    try {
      // Get current user ID and site code
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        throw new Error('Unable to identify user. Please login again.');
      }

      const token = getAuthToken();
      const siteCode = getSiteCode();

      // Use backend Excel export endpoint
      const response = await fetch(
        `${API_BASE_URL}/export-to-excel/master/${siteCode}/`,
        {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ dates: dates || [] })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Export failed: ${response.status}`);
      }

      // Download Excel file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const dateRange = dates && dates.length > 0
        ? `${dates[0]}_to_${dates[dates.length - 1]}`
        : new Date().toISOString().split('T')[0];
      const sheetName = 'Daily Report';
      a.download = `${sheetName} - ${siteCode} - ${dateRange}.xlsx`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      return {
        ok: true,
        status: 200,
        data: null,
        error: null
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  },

  /**
   * Load all Master Sheet entries for the current user
   */
  loadByUser: async (args: LoadByUserArg): Promise<ApiResponseWithHeaders<MasterSheetDataRow[]>> => {
    try {
      const ctx = normalizeLoadContext(args);
      if (!Number.isFinite(ctx.userId)) {
        return { ok: false, status: 400, data: null, error: 'Invalid user ID for Master Sheet load' };
      }

      // Check cache first
      const cacheKey = getCacheKey('mastersheet', ctx.userId, ctx.siteCode);
      const cached = getFromCache<MasterSheetDataRow[]>(cacheKey);
      if (cached) {
        return { ok: true, status: 200, data: cached, error: null };
      }

      const isAdminView = ctx.userId <= 0;
      const { start, end } = getLoadByUserDateRange();

      const response = await apiRequest<MasterSheetDataRow[]>('/master-sheet/range/', {
        params: { start_date: start, end_date: end, limit: 99999, ...(isAdminView ? { all_users: 'true' } : {}) }
      });

      if (response.ok && response.data) {
        // Backend handles role-based filtering via get_queryset(); no client-side re-filter needed.
        setCache(cacheKey, response.data);
        return { ok: true, status: 200, data: response.data, error: null };
      }

      return response;
    } catch (error: any) {
      return { ok: false, status: 0, data: null, error: error.message };
    }
  },

  /**
   * Load Master Sheet entries by date range with user/site filtering
   */
  loadByDateRange: async (ctx: NormalizedLoadContext & { startDate?: string; endDate?: string; offset?: number; limit?: number; cursorDate?: string; cursorId?: string }): Promise<ApiResponseWithHeaders<MasterSheetDataRow[]>> => {
    try {
      if (!Number.isFinite(ctx.userId)) {
        return { ok: false, status: 400, data: null, error: 'Invalid user ID for Master Sheet load' };
      }

      const isAdminView = ctx.userId <= 0;
      const start = ctx.startDate || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const end = ctx.endDate || new Date().toISOString().split('T')[0];

      const response = await apiRequest<MasterSheetDataRow[]>('/master-sheet/range/', {
        params: {
          start_date: start,
          end_date: end,
          offset: ctx.offset,
          limit: ctx.limit,
          cursor_date: ctx.cursorDate,
          cursor_id: ctx.cursorId
        }
      });

      if (response.ok && response.data) {
        // Backend handles role-based filtering via get_queryset(); no client-side re-filter needed.
        return { ok: true, status: 200, data: response.data, error: null, headers: response.headers };
      }

      return response;
    } catch (error: any) {
      return { ok: false, status: 0, data: null, error: error.message };
    }
  },

  /**
   * Aggregate data from other sheets and save to Master Sheet
   */
  aggregate: async (start_date: string, end_date: string, site_code?: string, only_dates_with_data: boolean = true, company_id?: number): Promise<ApiResponse<MasterSheetDataRow[]>> => {
    try {
      // Add company_id as optional argument
      const response = await apiRequest<{
        success: boolean;
        message: string;
        count: number;
        start_date: string;
        end_date: string;
        data: MasterSheetDataRow[];
      }>('/master-sheet/aggregate/', {
        method: 'POST',
        body: JSON.stringify({
          start_date,
          end_date,
          site_code: site_code || getSiteCode(),
          only_dates_with_data: only_dates_with_data,
          company_id: company_id
        })
      });

      if (response.ok && response.data) {
        return {
          ok: true,
          status: 200,
          data: response.data.data,
          error: null
        };
      }

      return {
        ok: false,
        status: response.status || 500,
        data: null,
        error: response.data?.message || 'Aggregation failed'
      };
    } catch (error: any) {
      return {
        ok: false,
        status: 0,
        data: null,
        error: error.message
      };
    }
  }
};

// ==================== HEALTH CHECK SERVICES ====================

export const healthService = {
  check: async (): Promise<ApiResponse<any>> => {
    return apiRequest<any>('/health');
  },

  ready: async (): Promise<ApiResponse<any>> => {
    return apiRequest<any>('/health/ready');
  },

  database: async (): Promise<ApiResponse<any>> => {
    return apiRequest<any>('/health/db');
  }
};

// ==================== PAGINATION HELPER ====================

/**
 * Pagination Loop Helper - Load all pages from a range endpoint
 * Automatically loops through paginated responses until all data is fetched
 * @param serviceFunc - Service function that returns {ok, data, headers} with X-Has-More header
 * @param options - Options with startDate, endDate, initialLimit
 * @returns Combined array of all rows from all pages
 */
export const loadAllPaginatedData = async (
  serviceFunc: (params: any) => Promise<{ ok: boolean; data: any[]; headers?: Record<string, string> }>,
  options: { startDate?: string; endDate?: string; initialLimit?: number } = {}
): Promise<any[]> => {
  const { startDate, endDate, initialLimit = 50 } = options;
  let allData: any[] = [];
  let hasMore = true;
  let offset = 0;
  let cursorDate: string | undefined;
  let cursorId: string | undefined;
  let pageCount = 0;

  while (hasMore) {
    pageCount++;
    const response = await serviceFunc({
      startDate,
      endDate,
      offset: cursorDate ? undefined : offset,
      limit: initialLimit,
      cursorDate,
      cursorId
    });

    if (!response.ok || !response.data) {
      break; // Stop if API error
    }

    allData = allData.concat(response.data);

    // Check X-Has-More header (backend indicates if more data available)
    const hasMoreHeader = response.headers?.['X-Has-More'] || response.headers?.['x-has-more'];
    hasMore = hasMoreHeader === 'true';

    const nextCursorDate = response.headers?.['X-Next-Cursor-Date'] || response.headers?.['x-next-cursor-date'];
    const nextCursorId = response.headers?.['X-Next-Cursor-Id'] || response.headers?.['x-next-cursor-id'];

    // Safety: Stop if we got fewer rows than requested (unlikely but possible)
    if (response.data.length < initialLimit) {
      hasMore = false;
    }

    if (nextCursorDate) {
      cursorDate = nextCursorDate;
      cursorId = nextCursorId || undefined;
    } else {
      offset += initialLimit;
    }

    // Safety limit to prevent infinite loops
    if (pageCount > 100) {
break;
    }
  }

  return allData;
};

// ==================== GENERAL SERVICES ====================

export const generalService = {
  getDgrData: async (siteCode: string, sheet?: string, options: { limit?: number; start_date?: string; end_date?: string } = {}): Promise<ApiResponse<any>> => {
    const { limit = 500, start_date, end_date } = options;
    let endpoint = `/get-dgr/${siteCode}/?limit=${limit}`;
    if (sheet) endpoint += `&sheet=${sheet}`;
    if (start_date) endpoint += `&start_date=${start_date}`;
    if (end_date) endpoint += `&end_date=${end_date}`;

    return apiRequest<any>(endpoint);
  }
};

// ==================== EXPORT ALL ====================

// Export cache utilities for external use (e.g., on site change, logout)
export { invalidateAllCache, invalidateCache };

export default {
  databankService,
  ambientService,
  bdDetailsService,
  meterReadingService,
  masterSheetService,
  healthService,
  generalService
};


